import { useCallback, useEffect, useRef, useState } from 'react'
import { useMount } from '~/shared/hooks/useMount'
import { Alert, Box, Button, Grid, Paper, Tooltip, Menu, MenuItem, Typography } from '@mui/material'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import MicNone from '@mui/icons-material/MicNone'
import VideocamOutlined from '@mui/icons-material/VideocamOutlined'
import SpeakerOutlined from '@mui/icons-material/SpeakerOutlined'
import { getDevices, startPreview, type PreviewHandle } from '@fireflyhealth/zoom-web'
import { useSnackbar } from 'notistack'

import { SessionToolbar } from './SessionToolbar'
import { SessionVideoTile } from './SessionVideoTile'
import { useZoomSession } from './SessionProvider'
import { useConnectionStrength, useHardwareMenusEls } from './utils'
import { fireAndForget } from '~/shared/lib/fireAndForget'
import { required } from '~/shared/lib/required'
import { logger } from '~/shared/lib/logger'

/**
 * The remedy for a blocked camera or microphone permission, worded as `app/`'s member call words
 * it: a browser's grant lives in its own per-site settings, so a provider is pointed there and told
 * to reload rather than at an OS settings screen that does not hold it.
 */
const blockedPermissionRemedy = (hardware: 'camera' | 'microphone') =>
  `Allow ${hardware} access for this site in your browser, then reload this page.`

export const SessionPreCall: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const { isPoorConnection, networkQuality } = useConnectionStrength()

  const {
    handleCloseAll,
    handleOpenAudio,
    handleOpenSpeakers,
    handleOpenVideo,
    audioMenuProps,
    speakersMenuProps,
    videoMenuProps,
  } = useHardwareMenusEls()

  const videoRef = useRef<HTMLVideoElement>(null)
  const {
    activeCamera,
    activeMicrophone,
    activeSpeaker,
    cameras,
    isMuted,
    isVideoOn,
    microphones,
    modal,
    setActiveCameraId,
    setActiveMicrophoneId,
    setActiveSpeakerId,
    setCameras,
    setIsMuted,
    setIsVideoOn,
    setMicrophones,
    setScreen,
    setSpeakers,
    speakers,
  } = useZoomSession()
  const previewRef = useRef<PreviewHandle | null>(null)
  const [tooltipDismissed, setTooltipDismissed] = useState(false)

  // Track camera/microphone permission so a blocked one can name its remedy rather than leaving an
  // empty device list to explain itself. The Permissions API reports both without opening a stream
  // and updates live if the provider changes the grant; browsers that do not expose it (Safari)
  // fall through the catch and the pickers simply stay empty, as before.
  const [permissionBlocked, setPermissionBlocked] = useState({ camera: false, microphone: false })
  useEffect(() => {
    const permissions = navigator.permissions
    if (!permissions?.query) return
    // `query` is async, so a fast unmount (screen swap or rejoin) can run this effect's cleanup
    // before it resolves. The `cancelled` flag makes an in-flight query a no-op once cleaned up, so
    // it never subscribes a `change` listener nothing will remove.
    let cancelled = false
    const cleanups: Array<() => void> = []
    const subscribe = async (kind: 'camera' | 'microphone') => {
      try {
        // `PermissionName` in TS's DOM lib omits camera/microphone though browsers accept them.
        const status = await permissions.query({ name: kind })
        if (cancelled) return
        const sync = () =>
          setPermissionBlocked(prev => ({ ...prev, [kind]: status.state === 'denied' }))
        sync()
        status.addEventListener('change', sync)
        cleanups.push(() => status.removeEventListener('change', sync))
      } catch {
        // Browsers without a Permissions entry for camera/microphone (e.g. Safari) reject here;
        // there is nothing to surface, and the pickers simply stay empty as before.
      }
    }
    void subscribe('camera')
    void subscribe('microphone')
    return () => {
      cancelled = true
      cleanups.forEach(cleanup => cleanup())
    }
  }, [])

  // Disable video if network is too slow. Declared after `useZoomSession` because naming
  // `setIsVideoOn` in the dependency array evaluates it during render.
  useEffect(() => {
    if (isPoorConnection) setIsVideoOn(false)
  }, [isPoorConnection, setIsVideoOn])

  // Stops the preview track. The handle only exists after a successful start, so this is a
  // no-op before one has run and idempotent afterwards.
  const handleStopCamera = useCallback(async () => {
    await previewRef.current?.stop()
  }, [])

  // Start the camera preview and store its handle
  const handleStartCamera = useCallback(
    async (cameraId: string) => {
      // It's possible for hardware to fail or get stuck, which requires user intervention.
      // Show feedback to user if this happens.
      const loggerTimeout = setTimeout(() => {
        enqueueSnackbar('Failed to start video. Try selecting a different camera or refreshing.', {
          variant: 'error',
        })
      }, 5000)

      try {
        previewRef.current = await startPreview(
          cameraId,
          required(videoRef.current, 'the preview video element'),
        )
      } catch (e) {
        // Likely the camera being toggled while it is still initializing.
        logger.info('[Zoom] camera not ready to toggle', e)
        enqueueSnackbar('Camera is starting', { variant: 'info' })
      }
      clearTimeout(loggerTimeout)
      return
    },
    [enqueueSnackbar],
  )

  // Toggle the camera on and off by clicking the camera icon
  const handleToggleCamera = useCallback(
    async (on: boolean) => {
      if (on) await handleStopCamera()
      else
        await handleStartCamera(
          required(activeCamera?.deviceId ?? cameras[0]?.deviceId, 'a camera'),
        )
      setIsVideoOn(!on)
    },
    [activeCamera, cameras, handleStartCamera, handleStopCamera, setIsVideoOn],
  )

  // Toggle between cameras via the dropdown menu
  const handleSwitchCamera = useCallback(
    async (cameraId: string) => {
      // If video is off, just switch the active camera
      if (!isVideoOn) {
        setActiveCameraId(cameraId)
        return
      }
      /**
       * Otherwise:
       * 1. Stop the current track
       * 2. Start a new the video track and start publishing
       * 3. Switch the active camera
       */
      await handleStopCamera()
      await handleStartCamera(cameraId)
      setActiveCameraId(cameraId)
    },
    [isVideoOn, handleStartCamera, handleStopCamera, setActiveCameraId],
  )

  // Enumerates devices once on mount and picks the defaults; the active camera, microphone and
  // speaker are read as they are at that moment.
  useMount(() => {
    getDevices()
      .then(({ cameras, microphones, speakers }) => {
        setSpeakers(speakers)
        setCameras(cameras)
        setMicrophones(microphones)
        /**
         * While speakers and microphone choices only exist in the dropdown menu in this view,
         * the camera choice is also responsible for starting the video track. Rather than just
         * setting the activeCameraId, we also start the video track if the user hasn't disabled
         * the camera prior to getDevices() resolving.
         */
        const [firstCamera] = cameras
        const [firstMicrophone] = microphones
        const [firstSpeaker] = speakers
        if (firstCamera)
          fireAndForget('[Zoom] switch camera', () =>
            handleSwitchCamera(activeCamera?.deviceId ?? firstCamera.deviceId),
          )
        if (firstMicrophone && !activeMicrophone) setActiveMicrophoneId(firstMicrophone.deviceId)
        if (firstSpeaker && !activeSpeaker) setActiveSpeakerId(firstSpeaker.deviceId)
      })
      .catch(e => {
        logger.error('[Zoom] init pre-call video', e)
      })
  })

  const handleJoinCall = useCallback(async () => {
    await handleStopCamera()
    setScreen('call')
  }, [handleStopCamera, setScreen])

  // Try to clean up the camera track on unmount, i.e. when exiting the video modal
  useEffect(
    () => () => {
      fireAndForget('[Zoom] stop camera', handleStopCamera)
    },
    [handleStopCamera],
  )

  return (
    <Grid container sx={{ alignContent: 'center', justifyContent: 'center', height: '100%' }}>
      <Grid size={{ xs: 12, lg: modal ? 12 : 6 }}>
        <Box
          sx={{
            p: 4,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{ overflow: 'hidden', position: 'relative', width: '100%', minHeight: 200 }}
          >
            <SessionVideoTile muted={isMuted} quality={networkQuality} />
            <Tooltip
              open={isPoorConnection && !tooltipDismissed}
              title="Video disabled due to poor connection"
            >
              <Box
                component="video"
                ref={videoRef}
                sx={{
                  display: 'block',
                  width: '100%',
                  position: 'relative',
                  zIndex: 1,
                  transform: 'scaleX(-1)',
                }}
                muted
              />
            </Tooltip>
            <Box
              sx={{
                background: 'linear-gradient(rgba(0,0,0,0), rgba(0,0,0,0.25))',
                position: 'absolute',
                bottom: 0,
                height: '25%',
                width: '100%',
              }}
            />
            <SessionToolbar
              onToggleMicrophone={muted => setIsMuted(!muted)}
              helpEvents={[]}
              newHelpEvent={false}
              onToggleCamera={on => {
                fireAndForget('[Zoom] toggle camera', () => handleToggleCamera(on))
                setTooltipDismissed(true)
              }}
            />
          </Paper>
          {/* Name the remedy for a blocked permission. Joining stays available either way (the
              button below is never disabled), so a provider whose camera is blocked but microphone
              is fine can still join with audio. */}
          {permissionBlocked.camera ? (
            <Alert severity="warning" sx={{ mt: 2, width: '100%' }}>
              {blockedPermissionRemedy('camera')}
            </Alert>
          ) : null}
          {permissionBlocked.microphone ? (
            <Alert severity="warning" sx={{ mt: 2, width: '100%' }}>
              {blockedPermissionRemedy('microphone')}
            </Alert>
          ) : null}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Button onClick={handleOpenAudio}>
                <MicNone fontSize="small" />
                <Typography sx={{ mx: 1 }} variant="inherit">
                  Microphone
                </Typography>
                <ArrowDropDown fontSize="small" />
              </Button>
              <Button onClick={handleOpenVideo}>
                <VideocamOutlined fontSize="small" />
                <Typography sx={{ mx: 1 }} variant="inherit">
                  Camera
                </Typography>
                <ArrowDropDown fontSize="small" />
              </Button>
              {speakers.length ? (
                <Button onClick={handleOpenSpeakers}>
                  <SpeakerOutlined fontSize="small" />
                  <Typography sx={{ mx: 1 }} variant="inherit">
                    Speakers
                  </Typography>
                  <ArrowDropDown fontSize="small" />
                </Button>
              ) : null}
              <Menu {...audioMenuProps}>
                {microphones.map(microphone => (
                  <MenuItem
                    key={microphone.deviceId}
                    selected={activeMicrophone?.deviceId === microphone.deviceId}
                    onClick={() => {
                      setActiveMicrophoneId(microphone.deviceId)
                      handleCloseAll()
                    }}
                  >
                    {microphone.label}
                  </MenuItem>
                ))}
              </Menu>
              <Menu {...speakersMenuProps}>
                {speakers.map(speaker => (
                  <MenuItem
                    selected={activeSpeaker?.deviceId === speaker.deviceId}
                    key={speaker.deviceId}
                    onClick={() => {
                      setActiveSpeakerId(speaker.deviceId)
                      handleCloseAll()
                    }}
                  >
                    {speaker.label}
                  </MenuItem>
                ))}
              </Menu>
              <Menu {...videoMenuProps}>
                {cameras.map(camera => (
                  <MenuItem
                    key={camera.deviceId}
                    selected={activeCamera?.deviceId === camera.deviceId}
                    onClick={() => {
                      fireAndForget('[Zoom] switch camera', () =>
                        handleSwitchCamera(camera.deviceId),
                      )
                      // setActiveCameraId(camera.deviceId)
                      handleCloseAll()
                    }}
                  >
                    {camera.label}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={() => fireAndForget('[Zoom] join call', handleJoinCall)}
            >
              Join Call
            </Button>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}
